import { supabase } from "@/integrations/supabase/client";

export interface Property {
  id: string;
  titulo: string;
  descripcion: string;
  precio: number;
  ubicacion: string;
  ciudad: string;
  metros_cuadrados: number;
  metros_parcela?: number;
  habitaciones: number;
  banos: number;
  garaje: boolean;
  plazas_garaje?: number;
  jardin: boolean;
  piscina: boolean;
  ano_construccion?: number;
  estado_conservacion: "nuevo" | "excelente" | "bueno" | "a_reformar";
  orientacion?: string;
  calefaccion?: string;
  carpinteria?: string;
  imagenes: string[];
  imagen_principal: string;
  carpeta_drive?: string;
  caracteristicas: string[];
  destacada: boolean;
  estado: "disponible" | "vendida" | "reservada";
}

export const defaultProperty: Omit<Property, "id"> = {
  titulo: "Villa moderna con piscina en C/ Ponent 35",
  descripcion:
    "Espectacular casa unifamiliar de 265 m² construidos sobre una parcela de 840 m² en una de las mejores zonas de Viladecavalls. Cuenta con amplios espacios, mucha luz natural, jardín privado con piscina y garaje para dos vehículos. Distribución en planta baja: salón-comedor de 40 m², cocina equipada, suite principal con baño en suite, y acceso directo al jardín. Planta superior con 3 dormitorios adicionales, baño completo y terraza con vistas. Acabados de alta calidad.",
  precio: 585000,
  ubicacion: "C/ Ponent 35, Viladecavalls",
  ciudad: "Viladecavalls",
  metros_cuadrados: 265,
  metros_parcela: 840,
  habitaciones: 4,
  banos: 2,
  garaje: true,
  plazas_garaje: 2,
  jardin: true,
  piscina: true,
  ano_construccion: 2005,
  estado_conservacion: "excelente",
  orientacion: "Sur-Oeste",
  calefaccion: undefined,
  carpinteria: "Aluminio con rotura de puente térmico",
  imagenes: [
    "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1200",
    "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200",
    "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200",
    "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=1200",
    "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1200",
    "/assets/plano-ponent35.jpg",
  ],
  imagen_principal:
    "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1200",
  carpeta_drive: "https://drive.google.com/drive/folders/example1",
  caracteristicas: [
    "Domótica",
    "Alarma perimetral",
    "Riego automático",
    "Barbacoa exterior",
    "Trastero",
  ],
  destacada: true,
  estado: "disponible",
};

function rowToProperty(row: any): Property {
  return {
    id: row.id,
    titulo: row.titulo,
    descripcion: row.descripcion,
    precio: Number(row.precio),
    ubicacion: row.ubicacion,
    ciudad: row.ciudad,
    metros_cuadrados: Number(row.metros_cuadrados),
    metros_parcela: row.metros_parcela ? Number(row.metros_parcela) : undefined,
    habitaciones: row.habitaciones,
    banos: row.banos,
    garaje: row.garaje,
    plazas_garaje: row.plazas_garaje ?? undefined,
    jardin: row.jardin,
    piscina: row.piscina,
    ano_construccion: row.ano_construccion ?? undefined,
    estado_conservacion: row.estado_conservacion as Property["estado_conservacion"],
    orientacion: row.orientacion ?? undefined,
    calefaccion: row.calefaccion ?? undefined,
    carpinteria: row.carpinteria ?? undefined,
    imagenes: row.imagenes || [],
    imagen_principal: row.imagen_principal || "",
    carpeta_drive: row.carpeta_drive ?? undefined,
    caracteristicas: row.caracteristicas || [],
    destacada: row.destacada,
    estado: row.estado as Property["estado"],
  };
}

export async function fetchProperties(): Promise<Property[]> {
  const { data, error } = await supabase
    .from("properties")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(rowToProperty);
}

export async function fetchProperty(id: string): Promise<Property | null> {
  const { data, error } = await supabase
    .from("properties")
    .select("*")
    .eq("id", id)
    .single();
  if (error) return null;
  return rowToProperty(data);
}

// Write operations have been removed from the public client.
// The site is a read-only public catalog; mutations must be performed
// by an admin via the database directly or via an authenticated server context.

export const formatPrice = (price: number) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(price);

// Legacy helpers kept for compatibility during migration - these are no longer used
// but prevent import errors if anything still references them
export const properties: Property[] = [];
export const getProperty = (_id: string): Property | undefined => undefined;
export const getAvailableProperties = (): Property[] => [];
export const getFeaturedProperties = (): Property[] => [];
export const addProperty = (p: Property) => p;
export const deleteProperty = (_id: string) => {};
export const updateProperty = (_id: string, _u: Partial<Property>) => undefined;
export const resetProperties = () => {};
